<template>
    <div class="shop-statistics">
        <div class="d-flex justify-content-between align-items-center mb-4">
            <div>
                <h5 class="mb-0">Statistik Shop</h5>
                <small class="text-muted">Order &amp; status semua waktu · pendapatan {{ periodLabel.toLowerCase() }}</small>
            </div>
            <select v-model="period" @change="loadStatistics" class="form-control form-control-sm" style="width: 160px;">
                <option value="7">7 Hari Terakhir</option>
                <option value="30">30 Hari Terakhir</option>
                <option value="90">3 Bulan Terakhir</option>
                <option value="all">Semua Waktu</option>
            </select>
        </div>

        <div v-if="loading && !loaded" class="text-center py-5">
            <div class="spinner-border text-primary"></div>
            <p class="text-muted mt-2 mb-0">Memuat statistik...</p>
        </div>

        <template v-else>
            <div class="row">
                <div class="col-md-3 col-6 mb-3" v-for="card in summaryCards" :key="card.key">
                    <div class="card text-center h-100 border radius-xl">
                        <div class="card-body py-3">
                            <i :class="card.icon + ' fa-2x mb-2 ' + card.color"></i>
                            <h3 class="mb-0" :style="card.small ? 'font-size:1.3rem' : ''">{{ card.value }}</h3>
                            <p class="text-muted mb-0 small">{{ card.label }}</p>
                            <small v-if="card.sub" class="text-muted">{{ card.sub }}</small>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row mt-2">
                <div class="col-md-8 mb-3">
                    <div class="card border radius-xl h-100">
                        <div class="card-header bg-white"><h6 class="mb-0">Grafik Pendapatan</h6></div>
                        <div class="card-body chart-wrap">
                            <canvas ref="revenueCanvas"></canvas>
                            <div v-if="!hasChartData" class="chart-empty text-muted">Belum ada data pendapatan</div>
                        </div>
                    </div>
                </div>
                <div class="col-md-4 mb-3">
                    <div class="card border radius-xl h-100">
                        <div class="card-header bg-white"><h6 class="mb-0">Status Order</h6></div>
                        <div class="card-body chart-wrap status-chart-wrap">
                            <canvas ref="statusCanvas"></canvas>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-8 mb-3">
                    <div class="card border radius-xl h-100">
                        <div class="card-header bg-white"><h6 class="mb-0">Transaksi per Hari</h6></div>
                        <div class="card-body chart-wrap">
                            <canvas ref="orderCanvas"></canvas>
                        </div>
                    </div>
                </div>
                <div class="col-md-4 mb-3">
                    <div class="card border radius-xl h-100">
                        <div class="card-header bg-white"><h6 class="mb-0">Top 5 Produk</h6></div>
                        <div class="card-body chart-wrap">
                            <canvas ref="topProductsCanvas"></canvas>
                            <div v-if="!topProducts.length" class="chart-empty text-muted">Belum ada penjualan</div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row">
                <div class="col-md-6 mb-3">
                    <div class="card border radius-xl">
                        <div class="card-header bg-white"><h6 class="mb-0">Detail Status Order</h6></div>
                        <div class="card-body">
                            <div v-for="row in statusRows" :key="row.key" class="d-flex justify-content-between mb-2">
                                <span>{{ row.label }}</span>
                                <span :class="'badge ' + row.badge">{{ stats[row.key] || 0 }}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-6 mb-3">
                    <div class="card border radius-xl">
                        <div class="card-header bg-white"><h6 class="mb-0">Breakdown Pendapatan</h6></div>
                        <div class="card-body">
                            <div class="d-flex justify-content-between mb-2"><span>Order Produk</span><b>{{ formatRupiah(stats.order_revenue) }}</b></div>
                            <div class="d-flex justify-content-between mb-2"><span>Donasi</span><b>{{ formatRupiah(stats.donation_revenue) }} <small class="text-muted">({{ stats.donation_count || 0 }}x)</small></b></div>
                            <div class="d-flex justify-content-between mb-2"><span>Kursus LMS</span><b>{{ formatRupiah(stats.lms_revenue) }} <small class="text-muted">({{ stats.lms_count || 0 }}x)</small></b></div>
                            <hr>
                            <div class="d-flex justify-content-between"><span><b>Total Net</b></span><b class="text-primary">{{ formatRupiah(stats.total_revenue) }}</b></div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, BarController, LineController, DoughnutController,
    Title, Tooltip, Legend, ArcElement, Filler,
} from 'chart.js';

ChartJS.register(
    CategoryScale, LinearScale, PointElement, LineElement, BarElement,
    BarController, LineController, DoughnutController,
    Title, Tooltip, Legend, ArcElement, Filler
);

export default {
    name: 'ShopStatistics',
    props: { shopId: { type: Number, required: true } },
    data() {
        return {
            period: '30',
            periodLabel: '30 Hari Terakhir',
            loading: false,
            loaded: false,
            stats: {},
            revenueChart: [],
            orderChart: [],
            topProducts: [],
            charts: { revenue: null, order: null, status: null, topProducts: null },
        };
    },
    computed: {
        hasChartData() {
            return this.revenueChart.some(d => d.revenue > 0);
        },
        summaryCards() {
            const s = this.stats;
            const txParts = [];
            if (s.total_donations) txParts.push(`${s.total_donations} donasi`);
            if (s.total_lms_enrollments) txParts.push(`${s.total_lms_enrollments} kursus`);
            const txSub = txParts.length ? `+ ${txParts.join(' · ')}` : '';
            return [
                { key: 'products', icon: 'fas fa-box', color: 'text-primary', label: 'Total Produk', value: s.total_products || 0 },
                { key: 'orders', icon: 'fas fa-shopping-cart', color: 'text-success', label: 'Total Order', value: s.total_orders || 0, sub: txSub },
                { key: 'completed', icon: 'fas fa-check-circle', color: 'text-info', label: 'Order Selesai', value: s.completed_orders || 0 },
                { key: 'revenue', icon: 'fas fa-money-bill-wave', color: 'text-warning', label: `Pendapatan (${this.periodLabel})`, value: this.formatRupiah(s.total_revenue), small: true },
            ];
        },
        statusChartData() {
            const s = this.stats;
            const rows = [
                { label: 'Belum Bayar', value: s.pending_orders || 0, color: '#f59e0b' },
                { label: 'Diproses', value: s.processing_orders || 0, color: '#667eea' },
                { label: 'Dikirim', value: s.shipped_orders || 0, color: '#3b82f6' },
                { label: 'Selesai', value: s.completed_orders || 0, color: '#10b981' },
                { label: 'Dibatalkan', value: s.cancelled_orders || 0, color: '#ef4444' },
            ];
            return {
                labels: rows.map(r => r.label),
                values: rows.map(r => r.value),
                colors: rows.map(r => r.color),
            };
        },
        statusRows() {
            return [
                { key: 'pending_orders', label: 'Belum Bayar', badge: 'badge-warning' },
                { key: 'processing_orders', label: 'Diproses', badge: 'badge-primary' },
                { key: 'shipped_orders', label: 'Dikirim', badge: 'badge-info' },
                { key: 'completed_orders', label: 'Selesai', badge: 'badge-success' },
                { key: 'cancelled_orders', label: 'Dibatalkan', badge: 'badge-danger' },
            ];
        },
    },
    mounted() { this.loadStatistics(); },
    beforeDestroy() {
        Object.values(this.charts).forEach(c => { if (c) c.destroy(); });
    },
    methods: {
        async loadStatistics() {
            this.loading = true;
            try {
                const r = await this.$store.dispatch('shop/shop_statistics', { id: this.shopId, period: this.period });
                const data = r.data.data;
                this.stats = data.stats || {};
                this.periodLabel = data.period_label || this.periodLabelFor(this.period);
                this.revenueChart = data.revenue_chart || [];
                this.orderChart = data.order_chart || [];
                this.topProducts = data.top_products || [];
                this.loaded = true;
                this.$nextTick(() => this.renderCharts());
            } catch (e) {
                this.$toast?.error?.('Gagal load statistik: ' + (e.response?.data?.message || e.message));
            } finally {
                this.loading = false;
            }
        },
        renderCharts() {
            this.renderRevenueChart();
            this.renderOrderChart();
            this.renderStatusChart();
            this.renderTopProductsChart();
        },
        renderRevenueChart() {
            const canvas = this.$refs.revenueCanvas;
            if (!canvas) return;
            if (this.charts.revenue) this.charts.revenue.destroy();
            const labels = this.revenueChart.map(d => d.label);
            const data = this.revenueChart.map(d => d.revenue);
            this.charts.revenue = new ChartJS(canvas.getContext('2d'), {
                type: 'line',
                data: {
                    labels,
                    datasets: [{
                        label: 'Pendapatan (Rp)',
                        data,
                        borderColor: '#667eea',
                        backgroundColor: 'rgba(102,126,234,0.15)',
                        tension: 0.3,
                        fill: true,
                    }],
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    plugins: { tooltip: { callbacks: { label: ctx => 'Rp ' + this.formatNumber(ctx.parsed.y) } } },
                    scales: { y: { beginAtZero: true, ticks: { callback: v => 'Rp ' + this.formatNumber(v) } } },
                },
            });
        },
        renderOrderChart() {
            const canvas = this.$refs.orderCanvas;
            if (!canvas) return;
            if (this.charts.order) this.charts.order.destroy();
            this.charts.order = new ChartJS(canvas.getContext('2d'), {
                type: 'bar',
                data: {
                    labels: this.orderChart.map(d => d.label),
                    datasets: [{
                        label: 'Jumlah Order',
                        data: this.orderChart.map(d => d.order_count),
                        backgroundColor: 'rgba(59,130,246,0.5)',
                        borderColor: '#3b82f6',
                        borderWidth: 1,
                    }],
                },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
                },
            });
        },
        renderStatusChart() {
            const canvas = this.$refs.statusCanvas;
            if (!canvas) return;
            if (this.charts.status) this.charts.status.destroy();
            const chart = this.statusChartData;
            this.charts.status = new ChartJS(canvas.getContext('2d'), {
                type: 'doughnut',
                data: {
                    labels: chart.labels,
                    datasets: [{
                        data: chart.values,
                        backgroundColor: chart.colors,
                        borderWidth: 1,
                        borderColor: '#fff',
                    }],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '55%',
                    plugins: {
                        legend: {
                            display: true,
                            position: 'bottom',
                            labels: {
                                boxWidth: 12,
                                boxHeight: 12,
                                padding: 12,
                                font: { size: 11 },
                                generateLabels: (chart) => {
                                    const ds = chart.data.datasets[0];
                                    return chart.data.labels.map((label, i) => ({
                                        text: `${label}: ${ds.data[i] || 0}`,
                                        fillStyle: ds.backgroundColor[i],
                                        strokeStyle: ds.backgroundColor[i],
                                        lineWidth: 0,
                                        hidden: false,
                                        index: i,
                                    }));
                                },
                            },
                        },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => `${ctx.label}: ${ctx.parsed}`,
                            },
                        },
                    },
                    layout: { padding: { bottom: 8 } },
                },
            });
        },
        periodLabelFor(period) {
            return { '7': '7 Hari Terakhir', '30': '30 Hari Terakhir', '90': '3 Bulan Terakhir', 'all': 'Semua Waktu' }[period] || '30 Hari Terakhir';
        },
        renderTopProductsChart() {
            const canvas = this.$refs.topProductsCanvas;
            if (!canvas || !this.topProducts.length) return;
            if (this.charts.topProducts) this.charts.topProducts.destroy();
            const labels = this.topProducts.map(p => p.product_name.length > 16 ? p.product_name.slice(0, 16) + '...' : p.product_name);
            this.charts.topProducts = new ChartJS(canvas.getContext('2d'), {
                type: 'bar',
                data: {
                    labels,
                    datasets: [{ label: 'Terjual', data: this.topProducts.map(p => p.qty_sold), backgroundColor: 'rgba(236,72,153,0.5)', borderColor: '#ec4899', borderWidth: 1 }],
                },
                options: { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { stepSize: 1 } } } },
            });
        },
        formatRupiah(v) { return 'Rp ' + this.formatNumber(v || 0); },
        formatNumber(v) { return new Intl.NumberFormat('id-ID').format(v); },
    },
};
</script>

<style scoped>
.chart-wrap { position: relative; height: 260px; }
.status-chart-wrap { height: 320px; padding-bottom: 12px; }
.chart-empty { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; }
</style>
